export type StatCardTone =
  | "info"
  | "positive"
  | "caution"
  | "danger"
  | "neutral";

const TONE_BORDER: Record<StatCardTone, string> = {
  info: "border-l-blue-100",
  positive: "border-l-green-100",
  caution: "border-l-orange",
  danger: "border-l-red-500",
  neutral: "border-l-grey-100",
};

interface StatCardProps {
  label: string;
  value?: string | number | null;
  detail?: string;
  tone?: StatCardTone;
  isLoading?: boolean;
  isError?: boolean;
}

/**
 * Compact KPI / metric card: an uppercase label, a large value, and an
 * optional supporting detail line, with a tone-colored left accent border.
 * Handles its own loading and error states so a single failed query degrades
 * gracefully instead of collapsing the dashboard.
 */
export default function StatCard({
  label,
  value,
  detail,
  tone = "neutral",
  isLoading = false,
  isError = false,
}: StatCardProps) {
  return (
    <div
      className={`rounded-xl border border-l-4 border-grey-100 bg-white p-4 shadow-sm ${TONE_BORDER[tone]}`}
      aria-busy={isLoading}
    >
      <p className="text-xs font-semibold uppercase tracking-wide text-grey-200">
        {label}
      </p>

      {isLoading ? (
        <div
          className="mt-2 h-9 w-24 animate-pulse rounded bg-gray-200"
          aria-hidden="true"
        />
      ) : isError ? (
        <p className="mt-2 text-lg font-semibold text-grey-200">Unavailable</p>
      ) : (
        <p className="mt-2 text-3xl font-semibold tabular-nums text-blue-200">
          {value}
        </p>
      )}

      {isError ? (
        <p className="mt-1 text-pretty text-sm text-grey-200">
          Could not load this metric right now.
        </p>
      ) : (
        detail && (
          <p className="mt-1 text-pretty text-sm text-grey-200">{detail}</p>
        )
      )}
    </div>
  );
}
